/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState } from 'react';
import BigNumber from 'bignumber.js';

import * as Styles from '@components/modals/styles';
import { SelectorCurrency } from '@components/selector-currency';
import { ILooseObject } from '@interface/loose-object';
import { swapCreate } from '@lib/beacon';
import { TezToMut } from '@utils/format';
import { testNodeHealth } from '@utils/node';
import { AlertDialog, Field, Input, RadioGroup, Text, Toggle } from '@versumstudios/ui';

export interface ISwapCreate {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  platformName?: string;
  tokenKT?: string;
  tokenId?: number;
  tokensOwned?: number;
  creatorsTotal?: number;
}

enum MODE {
  SIMPLE = 'simple',
  TIME_LIMITED = 'time-limited',
  ADVANCED = 'advanced',
}

export const SwapCreate: FC<ISwapCreate> = ({
  open,
  onOpenChange,
  platformName,
  tokenKT,
  tokenId,
  tokensOwned,
  creatorsTotal,
}) => {
  const [limit, setLimit] = useState(false);
  const [burn, setBurn] = useState(false);
  const [currentMode, setCurrentMode] = useState(MODE.SIMPLE as string);
  const [values, setValues] = useState<ILooseObject>({
    quantity: { error: false, data: tokensOwned && tokensOwned > 1 ? '' : tokensOwned },
    currency: { error: false, data: null },
    price: { error: false, data: '' },
    start: { error: false, data: '' },
    end: { error: false, data: '' },
    duration: { error: false, data: '' },
    limit: { error: false, data: '' },
  });

  const handleChange = (key: string, value: any) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: false, data: value },
    }));
  };

  const handleChangeMode = (nextMode: string) => {
    setCurrentMode(nextMode);

    handleChange('price', '');
    handleChange('start', '');
    handleChange('end', '');
    handleChange('duration', '');
    handleChange('limit', '');
  };

  const setError = (key: string, message: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: message, data: prevValues[key].data },
    }));
  };

  const validate = () => {
    let valid = true;

    if (!values.quantity.data) {
      setError('quantity', '"Quantity" is missing');
      valid = false;
    }

    if (Math.round(values.quantity.data) > Number(tokensOwned)) {
      setError('quantity', `You only own ${tokensOwned} tokens`);
      valid = false;
    }

    if (currentMode === MODE.SIMPLE) {
      if (Math.round(values.quantity.data) < 1) {
        setError('quantity', 'Minimum quantity 1 tokens');
        valid = false;
      }

      if (!values.price.data) {
        setError('price', '"Price" is missing');
        valid = false;
      }
    } else if (currentMode === MODE.TIME_LIMITED) {
      if (Math.round(values.quantity.data) < 1) {
        setError('quantity', 'Minimum quantity 1 tokens');
        valid = false;
      }

      if (!values.price.data) {
        setError('price', '"Price" is missing');
        valid = false;
      }

      if (!values.duration.data) {
        setError('duration', '"Duration" is missing');
        valid = false;
      }

      if (Number(values.duration.data) < 2) {
        setError('duration', 'Min "Duration" value is 2h');
        valid = false;
      }

      if (Number(values.duration.data) > 24 * 7) {
        setError('duration', 'Max "Duration" value is one-week (168h)');
        valid = false;
      }
    } else if (currentMode === MODE.ADVANCED) {
      if (Math.round(values.quantity.data) < 2) {
        setError('quantity', 'Minimum quantity 2 tokens');
        valid = false;
      }

      if (!values.start.data) {
        setError('start', '"Start price" is missing');
        valid = false;
      }

      if (!values.end.data) {
        setError('end', '"End price" is missing');
        valid = false;
      }

      if (Number(values.start.data) > Number(values.end.data)) {
        setError('end', '"End price" can\'t be smaller that "Start price"');
        valid = false;
      }

      // check if increment is divisible
      const range = new BigNumber(Number(values.end.data) - Number(values.start.data))
        .dividedBy(Number(values.quantity.data) - 1)
        .toNumber();

      if (range * Number(values.quantity.data) !== Number(values.end.data)) {
        setError('start', 'Range between start and end price not possible.');
        valid = false;
      }
    }

    if (limit) {
      if (!values.limit.data) {
        setError('limit', '"Editions per transaction" is missing');
        valid = false;
      }

      if (Math.round(values.limit.data) < 1) {
        setError('limit', '"Editions per transaction" should be bigger than 1');
        valid = false;
      }

      if (Math.round(values.limit.data) > Number(tokensOwned)) {
        setError('limit', `You only own ${tokensOwned} tokens`);
        valid = false;
      }
    }

    return valid;
  };

  const handleConfirm = async () => {
    if (validate()) {
      await testNodeHealth();

      if (currentMode === MODE.SIMPLE) {
        swapCreate(
          false,
          limit ? Number(values.limit.data) : 0,
          TezToMut(values.price.data),
          TezToMut(values.price.data),
          Number(values.quantity.data),
          null,
          platformName!,
          tokenKT!,
          tokenId!,
          creatorsTotal
        );
      } else if (currentMode === MODE.TIME_LIMITED) {
        swapCreate(
          burn,
          limit ? Number(values.limit.data) : 0,
          TezToMut(values.price.data),
          TezToMut(values.price.data),
          Number(values.quantity.data),
          Number(values.duration.data),
          platformName!,
          tokenKT!,
          tokenId!,
          creatorsTotal
        );
      } else if (currentMode === MODE.ADVANCED) {
        swapCreate(
          false,
          limit ? Number(values.limit.data) : 0,
          TezToMut(values.start.data),
          TezToMut(values.end.data),
          Number(values.quantity.data),
          null,
          platformName!,
          tokenKT!,
          tokenId!,
          creatorsTotal
        );
      }

      if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  const handleCancel = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog
      title="Create Swap"
      description="You're about to swap this token."
      open={open}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    >
      <Styles.Content>
        <RadioGroup
          value={currentMode}
          onChange={handleChangeMode}
          data={[
            {
              key: MODE.SIMPLE,
              label: 'Simple',
            },
            {
              key: MODE.TIME_LIMITED,
              label: 'Time-limited',
            },
            {
              key: MODE.ADVANCED,
              label: 'Advanced',
            },
          ]}
        />

        {tokensOwned && tokensOwned > 1 && (
          <>
            <Field uid="quantity" label="Quantity" error={values.quantity.error}>
              <Input
                type="number"
                min={currentMode === MODE.ADVANCED ? 2 : 1}
                max={tokensOwned}
                value={values.quantity.data}
                placeholder="e.g.: 1"
                onChange={(value: string) => handleChange('quantity', value)}
              />
            </Field>

            {Number(values.quantity.data) > 1 && (
              <Field
                uid="limit-toggle"
                label="Limit per transaction"
                tooltip="Limits the amount of editions that can be collected per transaction."
              >
                <Toggle value={limit} onChange={(nextValue: boolean) => setLimit(nextValue)} />
              </Field>
            )}

            {limit && (
              <Field
                uid="limit"
                label="Editions per transaction"
                tooltip="Limits the amount of editions that can be collected per transaction."
                error={values.limit.error}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g.: 2"
                  value={values.limit.data}
                  onChange={(value: string) => handleChange('limit', value)}
                />
              </Field>
            )}
          </>
        )}

        {(currentMode === MODE.SIMPLE || currentMode === MODE.TIME_LIMITED) && (
          <>
            <Field uid="currency" label="Currency" error={values.currency.error}>
              <SelectorCurrency onChange={(value: string) => handleChange('currency', value)} />
            </Field>
            <Field uid="price" label="Price" error={values.price.error}>
              <Input
                type="number"
                min={0}
                placeholder="e.g.: 7"
                onChange={(value: string) => handleChange('price', value)}
              />
            </Field>
          </>
        )}

        {currentMode === MODE.TIME_LIMITED && (
          <>
            <Field
              uid="duration"
              label="Time (hours)"
              error={values.duration.error}
              tooltip="Duration for the swap in hours."
            >
              <Input
                type="number"
                min={2}
                max={24 * 7}
                placeholder="e.g.: 24"
                value={values.duration.data}
                onChange={(value: string) => handleChange('duration', value)}
              />
            </Field>

            {Number(values.quantity.data) > 1 && (
              <Field uid="burn" label="Burn on end" tooltip="When the swap ends all remaining tokens will be burned.">
                <Toggle value={burn} onChange={(nextValue: boolean) => setBurn(nextValue)} />
              </Field>
            )}

            {burn && (
              <>
                <Text css={{ fontSize: 14, fontWeight: 700 }}>⚠️ Warning</Text>
                <Text css={{ fontSize: 14, color: '$secondaryFill' }}>
                  This option will burn any unsold tokens when the time is reached. Cancelling the swap also triggers
                  the burn. Make sure you&apos;re happy with the price.
                </Text>
              </>
            )}
          </>
        )}

        {currentMode === MODE.ADVANCED && (
          <>
            <Field uid="currency" label="Currency" error={values.currency.error}>
              <SelectorCurrency onChange={(value: string) => handleChange('currency', value)} />
            </Field>

            <Field uid="start" label="Start price" error={values.start.error}>
              <Input
                type="number"
                min={0}
                placeholder="e.g.: 1"
                value={values.start.data}
                onChange={(value: string) => handleChange('start', value)}
              />
            </Field>

            <Field uid="end" label="End price" error={values.end.error}>
              <Input
                type="number"
                min={0}
                placeholder="e.g.: 10"
                value={values.end.data}
                onChange={(value: string) => handleChange('end', value)}
              />
            </Field>
          </>
        )}
      </Styles.Content>
    </AlertDialog>
  );
};
